import { useState } from "react";
import { toast } from "sonner";
import { Eye, Search, RotateCw } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { Pill } from "./shared";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const when = (s) => new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const Viewer = ({ id, onClose }) => {
  const { data, error } = useFetch(id ? `/email-log/${id}` : null);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#121824] border-[#1F2A3E] text-slate-100" data-testid="email-log-viewer">
        <DialogHeader><DialogTitle data-testid="email-log-viewer-subject">{data?.subject || "Email"}</DialogTitle></DialogHeader>
        {error && <p className="text-sm text-red-400">{error}</p>}
        {data && <>
          <div className="text-xs text-slate-400" data-testid="email-log-viewer-meta">To {data.to} · {when(data.at)} · {data.ok ? "Delivered to mail service" : data.resent_ok ? `Failed, then resent ${when(data.resent_at)}` : "Failed"}</div>
          <iframe title="Sent email" sandbox="" srcDoc={`<body style="margin:0;background:#fff">${data.html}</body>`} className="w-full h-[60vh] rounded-md bg-white" data-testid="email-log-viewer-frame" />
        </>}
      </DialogContent>
    </Dialog>);
};

export const EmailLog = () => {
  const api = useApi();
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  const { data, error, reload } = useFetch(`/email-log?page=${page}&failed=${failed}&q=${encodeURIComponent(applied)}`);
  const search = (e) => { e.preventDefault(); setPage(1); setApplied(q); };
  const resend = async (r) => {
    setBusy(r.id);
    try { await api.post(`/email-log/${r.id}/resend`); toast.success(`Resent to ${r.to}`); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  const resendAll = async () => {
    if (!window.confirm(`Resend ${data.failed_pending} failed email(s)? They go out one by one over the next minute or two.`)) return;
    try { const r = await api.post("/email-log/resend-failed"); toast.success(`Resending ${r.data.queued} email(s). Refresh in a minute to see the results.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-4" data-testid="email-log">
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={search} className="flex gap-2 w-full max-w-lg">
          <input className="c-input" placeholder="Search by recipient or subject" value={q} onChange={(e) => setQ(e.target.value)} data-testid="email-log-search-input" />
          <Button type="submit" variant="outline" className="border-[#1F2A3E] hover:bg-[#1A2234]" data-testid="email-log-search-btn"><Search size={14} /></Button>
        </form>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer"><input type="checkbox" checked={failed} onChange={(e) => { setFailed(e.target.checked); setPage(1); }} data-testid="email-log-failed-only" />Failed only</label>
        {data?.failed_pending > 0 && <Button size="sm" onClick={resendAll} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="email-log-resend-all-btn"><RotateCw size={13} className="mr-1.5" />Resend all failed ({data.failed_pending})</Button>}
        <Button size="sm" variant="ghost" onClick={reload} className="text-slate-400 hover:text-white" data-testid="email-log-refresh-btn">Refresh</Button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="c-surface overflow-x-auto">
        <table className="c-table w-full text-sm">
          <thead><tr><th>Sent</th><th>To</th><th>Subject</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {(data?.items || []).map((r) => <tr key={r.id} data-testid="email-log-row">
              <td className="whitespace-nowrap text-slate-400">{when(r.at)}</td><td className="break-all">{r.to}</td><td>{r.subject}</td>
              <td><Pill tone={r.ok || r.resent_ok ? "green" : "red"}>{r.ok ? "Sent" : r.resent_ok ? "Resent" : "Failed"}</Pill></td>
              <td className="whitespace-nowrap"><div className="flex items-center gap-3">
                <button onClick={() => setOpen(r.id)} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300" data-testid="email-log-view-btn"><Eye size={13} />View</button>
                {!r.ok && !r.resent_ok && <button onClick={() => resend(r)} disabled={busy === r.id} className="inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200 disabled:opacity-50" data-testid="email-log-resend-btn">
                  <RotateCw size={13} className={busy === r.id ? "animate-spin" : ""} />{busy === r.id ? "Sending…" : "Resend"}</button>}
              </div></td>
            </tr>)}
            {data && !data.items.length && <tr><td colSpan={5} className="text-center text-slate-500 py-8" data-testid="email-log-empty">No emails sent yet{applied ? " matching your search" : ""}.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && data.pages > 1 && <div className="flex items-center gap-3 text-xs text-slate-400" data-testid="email-log-pager">
        <Button size="sm" variant="outline" className="border-[#1F2A3E]" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="email-log-prev">Previous</Button>
        Page {data.page} of {data.pages} · {data.total} emails
        <Button size="sm" variant="outline" className="border-[#1F2A3E]" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="email-log-next">Next</Button></div>}
      <Viewer id={open} onClose={() => setOpen(null)} />
    </div>);
};
